/** Buttons and form fields. */
import { css } from 'lit';

import { touchOrNarrow } from '../media.js';

export const formStyles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  button,
  .button {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--button-padding);
    border: var(--border-width) solid var(--border-strong);
    border-radius: var(--radius);
    background: var(--surface-raised);
    color: var(--text);
    font: inherit;
    font-size: var(--font-size-ui);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-ui);
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    min-height: var(--control-height);
    transition:
      background-color var(--duration-fast),
      border-color var(--duration-fast),
      transform var(--duration-fast);
  }

  button:hover:not(:disabled),
  .button:hover {
    border-color: var(--text);
    text-decoration: none;
  }

  button:active:not(:disabled),
  .button:active {
    transform: translateY(var(--press-offset));
  }

  button:focus-visible,
  .button:focus-visible,
  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible {
    outline: var(--focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  button:disabled {
    opacity: var(--disabled-opacity);
    cursor: not-allowed;
  }

  button.primary,
  .button.primary {
    background: var(--action);
    border-color: var(--action);
    color: var(--action-text);
  }

  button.primary:hover:not(:disabled),
  .button.primary:hover {
    background: var(--action-hover);
    border-color: var(--action-hover);
  }

  button.danger {
    color: var(--tone-danger);
  }

  button.danger:hover:not(:disabled) {
    border-color: var(--tone-danger);
    background: var(--tone-danger-soft);
  }

  button.small,
  .button.small {
    min-height: var(--control-height-small);
    padding: var(--button-padding-small);
    font-size: var(--font-size-sm);
  }

  .field {
    display: grid;
    gap: var(--space-1);
    margin-bottom: var(--space-4);
  }

  .field label,
  .field .label {
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-ui);
  }

  .field .help {
    color: var(--text-muted);
    font-size: var(--font-size-md);
  }

  .field .problem {
    color: var(--tone-danger);
    font-size: var(--font-size-md);
  }

  .field.invalid input,
  .field.invalid select,
  .field.invalid textarea {
    border-color: var(--tone-danger);
  }

  input[type='text'],
  input[type='email'],
  input[type='password'],
  select,
  textarea {
    width: 100%;
    padding: var(--field-padding);
    border: var(--border-width) solid var(--border-strong);
    border-radius: var(--radius);
    background: var(--surface-raised);
    color: var(--text);
    font: inherit;
    transition: border-color var(--duration-fast);
  }

  input[type='text']:hover,
  input[type='email']:hover,
  input[type='password']:hover,
  select:hover,
  textarea:hover {
    border-color: var(--text-muted);
  }

  /* As tall as a button: a select draws its line-height its own way, a height of its own would
     leave it shorter than the input above it. */
  input[type='text'],
  input[type='email'],
  input[type='password'],
  select {
    min-height: var(--control-height);
  }

  input[type='checkbox'] {
    accent-color: var(--action);
    width: var(--checkbox-size);
    height: var(--checkbox-size);
  }

  textarea {
    font-family: var(--font-mono);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-relaxed);
    resize: vertical;
  }

  /* Safari on iOS zooms the page into any field set below 16px when it takes focus, and does not
     zoom back out. On a phone every field is at least that, wherever it sits (a table, a band). */
  @media ${touchOrNarrow} {
    input[type='text'],
    input[type='email'],
    input[type='password'],
    select,
    textarea {
      font-size: var(--font-size-touch-field);
    }
  }

  /* A finger, not a pointer: every button and field at least --touch-target high, the small
     buttons included, and a checkbox big enough to see where it is. */
  @media ${touchOrNarrow} {
    button,
    .button,
    button.small,
    .button.small,
    input[type='text'],
    input[type='email'],
    input[type='password'],
    select {
      min-height: var(--touch-target);
    }

    button,
    .button {
      justify-content: center;
    }

    input[type='checkbox'] {
      width: var(--checkbox-size-touch);
      height: var(--checkbox-size-touch);
    }
  }

  .checkbox {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-weight: var(--font-weight-normal);
  }

  /* The items of a plane-menu: the rows of a list, not buttons. */
  plane-menu > button,
  plane-menu > .button {
    display: flex;
    width: 100%;
    justify-content: flex-start;
    padding: var(--space-2) var(--space-4);
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-weight: var(--font-weight-normal);
  }

  plane-menu > button:hover:not(:disabled),
  plane-menu > .button:hover {
    background: var(--row-hover);
  }

  plane-menu > button:active:not(:disabled),
  plane-menu > .button:active {
    transform: none;
  }

  plane-menu > button:focus-visible,
  plane-menu > .button:focus-visible {
    outline-offset: calc(var(--border-width) * -2);
  }

  .form-error {
    color: var(--tone-danger);
  }
`;
